import React, { useState, useRef, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useOutsideClick } from "hooks/useOutsideClick";
import { SelectProps } from "./Select.props";
import SelectItem from "./SelectItem";
import P from "../P/P";
import SvgIcon from "../SvgIcon/SvgIcon";
import { ChevronDownIcon } from "@assets/icons";
import Input from "../Input/Input";

/**
 * ✅ Removes accents & Turkish combining dot problem
 * Example: İ -> i , ğ -> g , ş -> s
 */
const stripDiacritics = (s: string) =>
	s.normalize("NFKD").replace(/[\u0300-\u036f]/g, "");

/**
 * ✅ Search normalization:
 * makes I / İ / i / ı searchable as the same character
 */
const normalizeForSearch = (s: string) =>
	stripDiacritics(
		String(s ?? "")
			.trim()
			.toLowerCase(),
	).replace(/ı/g, "i");

const Select: React.FC<SelectProps> = ({
	options,
	value,
	onChange,
	placeholder = "",
	label,
	disabled = false,
	className = "",
	itemClassName = "",
	menuClassName = "",
	isMulti = false,
	multiValue,
	onMultiChange,
	searchable = false,
	...props
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState("");
	const selectRef = useRef<HTMLDivElement>(null);

	useOutsideClick(selectRef, () => setIsOpen(false));

	/**
	 * ✅ Turkish-safe filtering
	 */
	const filteredOptions = useMemo(() => {
		if (!searchable) return options;

		const term = normalizeForSearch(searchTerm);
		if (!term) return options;

		return options.filter((opt: any) => {
			const label = normalizeForSearch(opt?.label);
			const value = normalizeForSearch(opt?.value);
			return label.includes(term) || value.includes(term);
		});
	}, [options, searchable, searchTerm]);

	const handleSelectItem = (item: any) => {
		if (disabled) return;

		if (isMulti) {
			const current = Array.isArray(multiValue) ? multiValue : [];
			const exists = current.find((v) => v.value === item.value);
			const updated = exists
				? current.filter((v) => v.value !== item.value)
				: [...current, item];

			onMultiChange?.(updated);
		} else {
			onChange && onChange(item);
			setIsOpen(false);

			// ✅ keep input synced with selected label
			if (searchable) setSearchTerm(item.label);
		}
	};

	const isSelected = (item: any) => {
		if (isMulti && Array.isArray(multiValue)) {
			return multiValue.some((v) => v.value === item.value);
		}
		return value?.value === item.value;
	};

	const renderLabel = () => {
		if (isMulti && Array.isArray(multiValue) && multiValue.length > 0) {
			return multiValue.map((v) => v.label).join(", ");
		}
		return value?.label || placeholder;
	};

	/**
	 * ✅ When dropdown closes, sync selected label back
	 */
	useEffect(() => {
		if (!isOpen && searchable) {
			setSearchTerm(value?.label || "");
		}
	}, [isOpen, value, searchable]);

	return (
		<div className={`w-full relative ${className}`} ref={selectRef} {...props}>
			{label && (
				<P
					size="t1"
					type="regular"
					className="mb-1 text-light-textColor dark:text-dark-textColor"
				>
					{label}
				</P>
			)}

			<div
				className={`flex items-center justify-between px-4 py-2 border rounded-lg cursor-pointer transition-colors duration-300 bg-light-bgColor dark:bg-dark-bgColor ${
					disabled
						? "border-grayTwo bg-grayOne cursor-not-allowed"
						: isOpen
							? "border-mainColor"
							: "border-stroke bg-white"
				}`}
				onClick={() => !disabled && setIsOpen((o) => !o)}
				aria-disabled={disabled}
				tabIndex={disabled ? -1 : 0}
				role="button"
			>
				{searchable ? (
					<Input
						className="p-0 inset-0 w-full h-full bg-transparent border-none outline-none text-light-textColor dark:text-dark-textColor placeholder-light-grayThree dark:placeholder-dark-grayThree focus:ring-0"
						placeholder={placeholder}
						value={isOpen ? searchTerm : value?.label || ""}
						onChange={(e) => {
							setSearchTerm(e.target.value);
							if (!isOpen) setIsOpen(true);
						}}
						onClick={(e) => {
							e.stopPropagation();
							!disabled && setIsOpen(true);
						}}
						disabled={disabled}
					/>
				) : (
					<P
						size="t1"
						type="regular"
						className={`truncate ${
							value
								? "text-light-textColor dark:text-dark-textColor"
								: "text-light-grayThree dark:text-dark-grayThree"
						}`}
					>
						{renderLabel()}
					</P>
				)}

				<motion.div
					className="transform ml-2"
					animate={isOpen ? { rotate: 0 } : { rotate: -90 }}
					transition={{ duration: 0.3 }}
				>
					<SvgIcon Icon={ChevronDownIcon} />
				</motion.div>
			</div>

			<AnimatePresence>
				{isOpen && (
					<motion.ul
						initial={{ opacity: 0, y: -10 }}
						animate={{ opacity: 1, y: 0 }}
						exit={{ opacity: 0, y: -10 }}
						transition={{ duration: 0.2 }}
						className={`absolute z-40 w-full mt-1 p-2 grid gap-1 bg-light-bgColor dark:bg-dark-bgColor border border-light-mainColor dark:border-dark-mainColor rounded-lg drop-shadow-shadowTwo max-h-48 overflow-auto ${menuClassName}`}
					>
						{filteredOptions.length ? (
							filteredOptions.map((item) => (
								<SelectItem
									key={item.value}
									item={item}
									onSelect={handleSelectItem}
									selected={isSelected(item)}
									itemClassName={itemClassName}
								/>
							))
						) : (
							<P size="t1" type="regular">
								No options
							</P>
						)}
					</motion.ul>
				)}
			</AnimatePresence>
		</div>
	);
};

export default React.memo(Select);
